import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import {
  type DocumentListItem,
  type DocumentListResponseApi,
  documentListItemSchema,
  documentListResponseSchema,
} from '@/entities/DocumentList';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';
import { schemeParseMultiple } from '@/utils/schemeParse';

type LatestDocumentsStoreParams = {
  organizationId: number;
};

const DEFAULT_LIMIT = 10;

export class LatestDocumentsStore implements ILocalStore {
  readonly meta = new MetaStore();
  readonly collection = new CollectionStore<DocumentListItem, DocumentListItem>({
    parseItem: (item) => item,
    getEntityKey: (item) => item.id,
  });

  private _organizationId: number;

  constructor({ organizationId }: LatestDocumentsStoreParams) {
    this._organizationId = organizationId;

    makeObservable<LatestDocumentsStore, '_organizationId'>(this, {
      _organizationId: observable,
      items: computed,
      fetchLatestDocuments: action.bound,
    });
  }

  get items(): DocumentListItem[] {
    return this.collection.items;
  }

  async fetchLatestDocuments(limit: number = DEFAULT_LIMIT): Promise<void> {
    this.meta.setLoadedStartMeta();

    const response = await call<DocumentListResponseApi>({
      endpoint: ENDPOINTS.documents.latest(String(this._organizationId)),
      data: { limit },
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();

      return;
    }

    const parsedResponse = schemeParse(documentListResponseSchema, response.data);

    if (!parsedResponse.success) {
      this.meta.setLoadedErrorMeta();

      return;
    }

    const parsed = schemeParseMultiple(documentListItemSchema, parsedResponse.data.items);

    runInAction(() => {
      this.collection.fromArray(parsed);
      this.meta.setLoadedSuccessMeta();
    });
  }

  destroy(): void {
    this.meta.destroy();
    this.collection.destroy();
  }
}
